import { Add as AddIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { PlanForm } from '../../lib/components/PlanForm';
import { useCurriculumStore } from '../../stores/curriculumStore';
import type { CurriculumPlan } from '../../types';
import { yearGroups } from './MockData';

type DialogMode = 'create' | 'edit' | 'view' | null;

export const CurriculumPlans: React.FC = () => {
  const { curriculumPlans, subjects, addCurriculumPlan, updateCurriculumPlan, deleteCurriculumPlan } =
    useCurriculumStore();

  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [selectedPlan, setSelectedPlan] = useState<CurriculumPlan | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [filterYearGroup, setFilterYearGroup] = useState<string>('all');

  // Filter plans by year group
  const filteredPlans =
    filterYearGroup === 'all' ? curriculumPlans : curriculumPlans.filter((p) => p.yearGroupId === filterYearGroup);

  // Stats
  const totalPlans = filteredPlans.length;
  const corePlans = filteredPlans.filter((p) => !p.constraints.isElective).length;
  const electivePlans = filteredPlans.filter((p) => p.constraints.isElective).length;

  const handleCreate = () => {
    setSelectedPlan(null);
    setDialogMode('create');
  };

  const handleEdit = (plan: CurriculumPlan) => {
    setSelectedPlan(plan);
    setDialogMode('edit');
  };

  const handleView = (plan: CurriculumPlan) => {
    setSelectedPlan(plan);
    setDialogMode('view');
  };

  const handleDelete = (plan: CurriculumPlan) => {
    setSelectedPlan(plan);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedPlan) {
      deleteCurriculumPlan(selectedPlan.id);
      setDeleteConfirmOpen(false);
      setSelectedPlan(null);
      if (dialogMode === 'view') {
        setDialogMode(null);
      }
    }
  };

  const handleFormSubmit = (data: CurriculumPlan) => {
    if (dialogMode === 'create') {
      const newPlan: CurriculumPlan = {
        ...data,
        id: `plan-${Date.now()}`,
      };
      addCurriculumPlan(newPlan);
    } else if (dialogMode === 'edit' && selectedPlan) {
      updateCurriculumPlan(selectedPlan.id, data);
    }
    setDialogMode(null);
    setSelectedPlan(null);
  };

  const handleCloseDialog = () => {
    setDialogMode(null);
    setSelectedPlan(null);
  };

  const getSubjectName = (subjectId: string) => {
    const subject = subjects.find((s) => s.id === subjectId);
    return subject?.name || 'Unknown Subject';
  };

  const getYearGroupName = (yearGroupId: string) => {
    const yearGroup = yearGroups.find((y) => y.value === yearGroupId);
    return yearGroup?.label || yearGroupId;
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Curriculum Plans
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
            Add Plan
          </Button>
        </Box>

        {/* Filter */}
        <Box sx={{ mb: 3 }}>
          <Select value={filterYearGroup} onChange={(e) => setFilterYearGroup(e.target.value)} sx={{ minWidth: 200 }}>
            <MenuItem value="all">All Year Groups</MenuItem>
            {yearGroups.map((yg) => (
              <MenuItem key={yg.value} value={yg.value}>
                {yg.label}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" color="text.secondary">
                Total Plans
              </Typography>
              <Typography variant="h3">{totalPlans}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" color="text.secondary">
                Core Subjects
              </Typography>
              <Typography variant="h3">{corePlans}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" color="text.secondary">
                Elective Subjects
              </Typography>
              <Typography variant="h3">{electivePlans}</Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Plans List */}
        <Paper sx={{ p: 2 }}>
          <Grid container spacing={2}>
            {filteredPlans.map((plan) => (
              <Grid item xs={12} sm={6} md={4} key={plan.id}>
                <Paper sx={{ p: 2, cursor: 'pointer' }} onClick={() => handleView(plan)}>
                  <Typography variant="h6">{getSubjectName(plan.subjectId)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {getYearGroupName(plan.yearGroupId)}
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip label={`${plan.lessonsPerWeek} lessons/week`} size="small" />
                    <Chip label={plan.teachingModel} size="small" color="primary" />
                    {plan.constraints.isElective && <Chip label="Elective" size="small" color="secondary" />}
                    {plan.constraints.requiresDoubleLessons && <Chip label="Double lessons" size="small" />}
                    {plan.constraints.requiresSpecialistRoom && <Chip label="Specialist room" size="small" />}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Create/Edit Dialog */}
        <Dialog
          open={dialogMode === 'create' || dialogMode === 'edit'}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{dialogMode === 'create' ? 'Add New Curriculum Plan' : 'Edit Curriculum Plan'}</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <PlanForm plan={selectedPlan || undefined} onSubmit={handleFormSubmit} onCancel={handleCloseDialog} />
            </Box>
          </DialogContent>
        </Dialog>

        {/* View Dialog */}
        <Dialog open={dialogMode === 'view'} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>{selectedPlan && getSubjectName(selectedPlan.subjectId)}</DialogTitle>
          <DialogContent>
            {selectedPlan && (
              <Box sx={{ pt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Year Group
                    </Typography>
                    <Typography variant="body1">{getYearGroupName(selectedPlan.yearGroupId)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Lessons Per Week
                    </Typography>
                    <Typography variant="body1">{selectedPlan.lessonsPerWeek}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Teaching Model
                    </Typography>
                    <Typography variant="body1">{selectedPlan.teachingModel}</Typography>
                  </Grid>
                  {selectedPlan.setCount && (
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Number of Sets
                      </Typography>
                      <Typography variant="body1">{selectedPlan.setCount}</Typography>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Constraints
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {selectedPlan.constraints.isElective && <Chip label="Elective" size="small" color="secondary" />}
                      {selectedPlan.constraints.requiresDoubleLessons && (
                        <Chip label={`Double lessons (${selectedPlan.constraints.doubleCount || 0})`} size="small" />
                      )}
                      {selectedPlan.constraints.requiresSpecialistRoom && (
                        <Chip
                          label={`Specialist room: ${selectedPlan.constraints.requiredRoomType || 'Any'}`}
                          size="small"
                        />
                      )}
                      {selectedPlan.constraints.optionBlock && (
                        <Chip label={`Option Block: ${selectedPlan.constraints.optionBlock}`} size="small" />
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Close</Button>
            <Button onClick={() => selectedPlan && handleEdit(selectedPlan)} variant="contained">
              Edit
            </Button>
            <Button onClick={() => selectedPlan && handleDelete(selectedPlan)} color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This will remove the curriculum plan from the timetable
            </Alert>
            <Typography>
              Are you sure you want to delete the plan for {selectedPlan && getSubjectName(selectedPlan.subjectId)}?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button onClick={handleConfirmDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default CurriculumPlans;
